<template>
  <FlashNotifications ref="flash" />

  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">

    <!-- ═══════════ Card header ═══════════ -->
    <div class="px-5 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
      <div class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
        <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <rect x="3" y="5" width="18" height="14" rx="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path stroke-linecap="round" stroke-linejoin="round" d="m4 7 8 6 8-6"/>
        </svg>
      </div>
      <div class="min-w-0 flex-1">
        <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ t.title }}</h2>
        <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none truncate">{{ t.subtitle }}</p>
      </div>
      <span v-if="data && canManage"
            class="flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
        {{ usageLabel }}
      </span>
    </div>

    <!-- ═══════════ Loading skeleton ═══════════ -->
    <div v-if="loading" class="animate-pulse divide-y divide-gray-100 dark:divide-gray-700/60">
      <div v-for="i in skeletonRows + 1" :key="i" class="px-5 sm:px-6 py-4 flex items-center justify-between gap-4">
        <div class="space-y-2 min-w-0 flex-1">
          <div class="h-3 w-48 max-w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="h-2.5 w-64 max-w-full bg-gray-100 dark:bg-gray-800 rounded"></div>
        </div>
        <div class="h-5 w-16 bg-gray-100 dark:bg-gray-800 rounded-full flex-shrink-0"></div>
      </div>
      <div class="px-5 sm:px-6 py-4 flex flex-col sm:flex-row gap-2">
        <div class="h-10 flex-1 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
        <div class="h-10 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
      </div>
    </div>

    <!-- ═══════════ Error ═══════════ -->
    <div v-else-if="storeError" class="flex flex-col items-center justify-center py-16 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ t.load_error }}</p>
      <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors">
        Try again
      </button>
    </div>

    <template v-else-if="data">

      <!-- ═══════════ Read only notice ═══════════ -->
      <div v-if="!canManage" class="px-5 sm:px-6 pt-4">
        <div class="flex items-start gap-2.5 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
          <svg class="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/>
          </svg>
          <div>
            <p class="text-xs font-semibold text-blue-800 dark:text-blue-300 leading-snug">{{ t.restricted_title }}</p>
            <p class="text-xs text-blue-700 dark:text-blue-400 mt-1 leading-relaxed">{{ restrictedBody }}</p>
          </div>
        </div>
      </div>

      <!-- ═══════════ Addresses ═══════════ -->
      <ul class="divide-y divide-gray-100 dark:divide-gray-700/60">
        <li class="px-5 sm:px-6 py-4">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm font-medium text-gray-900 dark:text-white break-all">{{ data.primary_email }}</span>
            <span class="px-2 py-0.5 rounded-full text-[11px] font-semibold" :class="BADGE_CLASSES.primary">{{ t.primary_badge }}</span>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ t.primary_help }}</p>
        </li>

        <li v-for="row in emails" :key="row.id"
            class="px-5 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-medium text-gray-900 dark:text-white break-all">{{ row.email }}</span>
              <span class="px-2 py-0.5 rounded-full text-[11px] font-semibold" :class="BADGE_CLASSES[row.status]">
                {{ t[`${row.status}_badge`] }}
              </span>
              <span v-if="isSignedInWith(row)"
                    class="px-2 py-0.5 rounded-full text-[11px] font-semibold" :class="BADGE_CLASSES.signed_in">
                {{ t.signed_in_badge }}
              </span>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ describe(row) }}</p>
          </div>

          <div v-if="canManage" class="flex items-center gap-2 flex-shrink-0">
            <template v-if="row.status === 'confirmed'">
              <button type="button" @click="askRemove(row)" :disabled="busyId === row.id"
                      class="px-3 py-1.5 border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 text-xs font-semibold rounded-lg transition-colors">
                {{ t.remove }}
              </button>
            </template>
            <template v-else>
              <button type="button" @click="resend(row)" :disabled="busyId === row.id || secondsUntilResend(row) > 0"
                      class="px-3 py-1.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-semibold rounded-lg transition-colors tabular-nums">
                {{ resendLabel(row) }}
              </button>
              <button type="button" @click="askRemove(row)" :disabled="busyId === row.id"
                      class="px-3 py-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50 text-xs font-semibold transition-colors">
                {{ t.cancel_request }}
              </button>
            </template>
          </div>
        </li>
      </ul>

      <!-- ═══════════ Add an email ═══════════ -->
      <div v-if="canManage" class="px-5 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-[#161b22]/60">
        <p v-if="atLimit" class="text-xs text-gray-500 dark:text-gray-400">{{ limitLabel }}</p>

        <form v-else @submit.prevent="startAdd">
          <label for="secondary-email-input" class="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ t.add_label }}
          </label>
          <div class="flex flex-col sm:flex-row gap-2">
            <input id="secondary-email-input" v-model="newEmail" type="email" autocomplete="email" spellcheck="false"
                   :placeholder="t.add_placeholder"
                   class="flex-1 min-w-0 px-3.5 py-2.5 text-sm border rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                   :class="addError ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'">
            <button type="submit" :disabled="saving || !newEmail.trim()"
                    class="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors">
              {{ t.add_cta }}
            </button>
          </div>
          <p v-if="addError" class="flex items-center gap-1 mt-2 text-xs text-red-500">
            <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
            {{ addError }}
          </p>
          <p v-else class="mt-2 text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed">{{ t.add_help }}</p>
        </form>
      </div>

    </template>
  </div>

  <!-- ═══════════ Confirm it is you ═══════════ -->
  <div v-if="reauthOpen" class="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50" @click.self="closeReauth">
    <div class="w-full max-w-sm bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-white">{{ t.reauth_title }}</h3>
      </div>

      <form @submit.prevent="submitAdd" class="px-5 py-4 space-y-3">
        <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed break-words">{{ reauthBody }}</p>

        <div>
          <label for="secondary-email-reauth" class="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ usePassword ? t.password_label : t.code_label }}
          </label>
          <input id="secondary-email-reauth" ref="reauthInput" v-model="reauthValue"
                 :type="usePassword ? 'password' : 'text'"
                 :inputmode="usePassword ? undefined : 'text'"
                 :autocomplete="usePassword ? 'current-password' : 'one-time-code'"
                 spellcheck="false"
                 class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
        </div>

        <p v-if="reauthError" class="flex items-center gap-1 text-xs text-red-500">
          <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
          {{ reauthError }}
        </p>

        <div class="flex items-center justify-end gap-2 pt-1">
          <button type="button" @click="closeReauth" :disabled="saving"
                  class="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-semibold transition-colors">
            {{ t.cancel }}
          </button>
          <button type="submit" :disabled="saving || !reauthValue"
                  class="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            {{ t.continue }}
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- ═══════════ Remove or cancel ═══════════ -->
  <div v-if="removing" class="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50" @click.self="removing = null">
    <div class="w-full max-w-sm bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-white break-words">{{ removeTitle }}</h3>
      </div>
      <div class="px-5 py-4 space-y-4">
        <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          {{ removing.status === 'confirmed' ? t.remove_body : t.cancel_request_body }}
        </p>
        <div class="flex items-center justify-end gap-2">
          <button type="button" @click="removing = null" :disabled="busyId === removing.id"
                  class="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-semibold transition-colors">
            {{ t.keep }}
          </button>
          <button type="button" @click="confirmRemove" :disabled="busyId === removing.id"
                  class="inline-flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50">
            <svg v-if="busyId === removing.id" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            {{ removing.status === 'confirmed' ? t.remove : t.cancel_request }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import { useSecondaryEmailsStore } from 'stores/secondary_emails.js'
import { getCachedSecondaryEmailsCount } from 'composables/useSecondaryEmailsCountCache.js'

const BASE_URL = '/api/v1/user/secondary_emails'
// The same loose shape the server checks (Devise.email_regexp). The server has
// the final say; this only saves a round trip for an obvious typo.
const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+$/

const BADGE_CLASSES = {
  primary: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  confirmed: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  pending: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  expired: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  signed_in: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
}

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { I18N, parseLocale, CURRENT_LOCALE } = useConstants()
const store = useSecondaryEmailsStore()
const t = I18N.secondary_emails.settings

const skeletonRows = getCachedSecondaryEmailsCount()

const data = computed(() => store.data)
const loading = computed(() => !store.data && !store.error)
const storeError = computed(() => store.error)
const emails = computed(() => store.data?.emails ?? [])
const canManage = computed(() => store.data?.can_manage === true)
const usePassword = computed(() => store.data?.requires_password !== false)
const atLimit = computed(() => emails.value.length >= (store.data?.limit ?? 0))

const usageLabel = computed(() => parseLocale(t.usage, { count: emails.value.length, limit: store.data?.limit }))
const limitLabel = computed(() => parseLocale(t.limit_reached, { limit: store.data?.limit }))
const restrictedBody = computed(() => parseLocale(t.restricted_body, { email: store.data?.primary_email }))

// Ticks once a second so relative times and the resend countdown stay live.
const now = ref(Date.now())
let clock = null

onMounted(() => {
  store.fetchIfStale()
  clock = setInterval(() => { now.value = Date.now() }, 1000)
})

onUnmounted(() => clearInterval(clock))

async function retry() {
  store.invalidate()
  store.error = null
  await store.fetchIfStale()
}

// redaxios rejects with the response on a non 2xx, so the server's own message
// sits one level deeper than on the success path.
function messageFrom(res) {
  return res?.response?.message || res?.error?.data?.message || t.generic_error
}

// ─── Describing rows ───

const dateFormat = new Intl.DateTimeFormat(CURRENT_LOCALE || undefined, { year: 'numeric', month: 'short', day: 'numeric' })
const relativeFormat = new Intl.RelativeTimeFormat(CURRENT_LOCALE || undefined, { numeric: 'auto' })
const UNITS = [['year', 31536000], ['month', 2592000], ['day', 86400], ['hour', 3600], ['minute', 60]]

function relativeTime(iso) {
  const seconds = (new Date(iso).getTime() - now.value) / 1000
  const unit = UNITS.find(([, size]) => Math.abs(seconds) >= size)
  return unit ? relativeFormat.format(Math.round(seconds / unit[1]), unit[0]) : relativeFormat.format(0, 'minute')
}

function describe(row) {
  if (row.status === 'confirmed') {
    const linked = parseLocale(t.linked_on, { date: dateFormat.format(new Date(row.confirmed_at)) })
    const used = row.last_signed_in_at ? parseLocale(t.last_used, { time: relativeTime(row.last_signed_in_at) }) : t.never_used
    return `${linked}, ${used}`
  }
  if (row.status === 'expired') return t.expired_help

  return `${parseLocale(t.sent_at, { time: relativeTime(row.confirmation_sent_at) })} ${parseLocale(t.expires_in, { time: relativeTime(row.expires_at) })}`
}

function isSignedInWith(row) {
  const identity = store.data?.login_identity
  return identity?.kind === 'secondary' && identity.email === row.email
}

// ─── Adding ───

const newEmail = ref('')
const addError = ref(null)
const saving = ref(false)
const reauthOpen = ref(false)
const reauthValue = ref('')
const reauthError = ref(null)
const reauthInput = ref(null)

const reauthBody = computed(() => parseLocale(t.reauth_body, { email: newEmail.value.trim() }))

async function startAdd() {
  addError.value = null
  if (!EMAIL_SHAPE.test(newEmail.value.trim())) {
    addError.value = t.invalid_email
    return
  }

  // An account with no password of its own and no 2FA has nothing to re-check.
  if (!store.data?.requires_reauthentication) {
    await submitAdd()
    return
  }

  reauthValue.value = ''
  reauthError.value = null
  reauthOpen.value = true
  await nextTick()
  reauthInput.value?.focus()
}

function closeReauth() {
  if (saving.value) return
  reauthOpen.value = false
  reauthValue.value = ''
  reauthError.value = null
}

async function submitAdd() {
  const payload = { email: newEmail.value.trim() }
  if (store.data?.requires_reauthentication) payload[usePassword.value ? 'password' : 'otp_code'] = reauthValue.value

  saving.value = true
  reauthError.value = null
  const res = await axiosRequest('POST', BASE_URL, payload, false)
  saving.value = false

  if (!res.js_error && res.response?.success) {
    store.upsertEmail(res.response.data)
    newEmail.value = ''
    closeReauth()
    flash.value?.show(res.response.message, 'notice')
    return
  }

  // A wrong password or code keeps the dialog open to try again. Anything else
  // is about the address itself, so it belongs under the input.
  if (res.error?.status === 401) {
    reauthError.value = messageFrom(res)
    return
  }

  closeReauth()
  addError.value = messageFrom(res)
}

// ─── Resending ───

const busyId = ref(null)

function secondsUntilResend(row) {
  if (!row.resend_available_at) return 0
  return Math.max(0, Math.ceil((new Date(row.resend_available_at).getTime() - now.value) / 1000))
}

function resendLabel(row) {
  const seconds = secondsUntilResend(row)
  return seconds > 0 ? parseLocale(t.resend_in, { seconds }) : t.resend
}

async function resend(row) {
  busyId.value = row.id
  const res = await axiosRequest('POST', `${BASE_URL}/${row.id}/resend`, {}, false)
  busyId.value = null

  if (!res.js_error && res.response?.success) {
    store.upsertEmail(res.response.data)
    flash.value?.show(res.response.message, 'notice')
    return
  }

  const availableAt = res.error?.data?.data?.resend_available_at
  if (availableAt) store.upsertEmail({ ...row, resend_available_at: availableAt })
  flash.value?.show(messageFrom(res), 'alert')
}

// ─── Removing ───

const removing = ref(null)

const removeTitle = computed(() => {
  if (!removing.value) return ''
  const template = removing.value.status === 'confirmed' ? t.remove_title : t.cancel_request_title
  return parseLocale(template, { email: removing.value.email })
})

function askRemove(row) {
  removing.value = row
}

async function confirmRemove() {
  const row = removing.value
  busyId.value = row.id
  const res = await axiosRequest('DELETE', `${BASE_URL}/${row.id}`, {}, false)
  busyId.value = null
  removing.value = null

  if (!res.js_error && res.response?.success) {
    store.setData(res.response.data)
    flash.value?.show(res.response.message, 'notice')
    return
  }

  flash.value?.show(messageFrom(res), 'alert')
}
</script>
